import { gqlMutate, gqlQuery } from '@hapana/shared/web/services/requests.service'
import type {
  AgeBandOutcome,
  AgeEvidenceRequired,
  AgeVerificationMethod,
  JurisdictionRuleStatus,
  PolicyAcceptanceMethod,
  PolicyAcceptanceState,
  PolicyEquipmentScope,
  PolicyRevisionStatus,
  PolicySignerRule,
} from '@hapana/shared/schemas/policy-compliance/policy-compliance-enums'
import type {
  ReadinessDimensionResult,
  ReadinessItem,
} from '@hapana/shared/schemas/policy-compliance/readiness-schemas'
import type {
  ApprovalPinStatus,
  PaperFormApprover,
  PaperPolicyFormSlotInput,
  PaperScanUploadTicket as PaperScanUploadTicketShape,
} from '@hapana/shared/schemas/policy-compliance/policy-evidence-schemas'
import type { PolicyComplianceSettingsView } from '@hapana/shared/schemas/policy-compliance/policy-compliance-settings-schemas'

import {
  POLICY_COMPLIANCE_QUERY_KEYS,
  sitePolicyComplianceSettingsQuery,
  updateSitePolicyComplianceSettingsMutation,
  cancelScheduledSitePolicyRevisionMutation,
  clientAgreementsPageQuery,
  clientPolicyAgreementsQuery,
  clientPolicyComplianceQuery,
  clientPolicyRelationsQuery,
  createGatingSitePolicyMutation,
  createPaperScanUploadMutation,
  deleteGatingSitePolicyMutation,
  equipmentJurisdictionRulesQuery,
  myApprovalPinStatusQuery,
  paperApproversQuery,
  publishSitePolicyRevisionMutation,
  recordPaperPolicyFormMutation,
  retireEquipmentJurisdictionRuleMutation,
  revokeGuardianAuthorityMutation,
  saveEquipmentJurisdictionRuleMutation,
  setMyApprovalPinMutation,
  sitePoliciesForGatingQuery,
  sitePolicyRevisionsQuery,
  updateGatingSitePolicyMutation,
  verifyClientAgeMutation,
  verifyMyApprovalPinMutation,
  voidPolicyAcceptanceMutation,
  withdrawElectronicConsentMutation,
  withdrawGuardianPermissionMutation,
} from './graphql/policy-compliance.queries'
import type { GraphqlWireShape } from './wire-shape.types'

export type GatingSitePolicy = {
  sitePolicyId: string
  siteId: string
  name: string
  content: string
  isRequiredToAgree: boolean
  requiredForEquipmentUse: boolean
  acceptanceMethod: PolicyAcceptanceMethod
  signerRule: PolicySignerRule
  guardianInPersonOnly: boolean
  equipmentScope: PolicyEquipmentScope[]
  renewalMonths: number | null
  updatedAt: string | null
  updatedByStaffUserName: string | null
}

export type GatingSitePolicyInput = {
  name: string
  content: string
  isRequiredToAgree: boolean
  requiredForEquipmentUse: boolean
  acceptanceMethod: PolicyAcceptanceMethod
  signerRule: PolicySignerRule
  guardianInPersonOnly: boolean
  equipmentScope: PolicyEquipmentScope[]
  renewalMonths: number | null
}

export type GatingSitePolicyUpdateInput = GatingSitePolicyInput & {
  requireReacceptance?: boolean
  effectiveFrom?: string
}

export type SitePolicyRevisionRow = {
  revisionId: string
  sitePolicyId: string
  siteId: string
  revisionNumber: number
  acceptanceGeneration: number
  status: PolicyRevisionStatus
  effectiveFrom: string | null
  name: string
  acceptanceMethod: PolicyAcceptanceMethod
  signerRule: PolicySignerRule
  guardianInPersonOnly: boolean
  requiredForEquipmentUse: boolean
  equipmentScope: PolicyEquipmentScope[]
  renewalMonths: number | null
  publishedAt: string
  cancelledAt: string | null
}

export type SitePolicyPublishResult = {
  impactCount: number
  cancelledRevisionIds: string[]
  revision: SitePolicyRevisionRow
}

export type GatingSitePolicyUpdateResult = GatingSitePolicy & {
  publishResult: SitePolicyPublishResult | null
}

export type JurisdictionAgeBandRow = {
  minAge: number
  maxAge: number | null
  outcome: AgeBandOutcome
}

export type JurisdictionRuleRow = {
  ruleId: string
  ruleRevision: number
  jurisdictionCode: string
  equipmentScope: PolicyEquipmentScope[]
  effectiveFrom: string
  effectiveTo: string | null
  ageEvidenceRequired: AgeEvidenceRequired
  ageBands: JurisdictionAgeBandRow[]
  status: JurisdictionRuleStatus
  notes: string | null
}

export type JurisdictionRuleInput = {
  jurisdictionCode: string
  equipmentScope: PolicyEquipmentScope[]
  effectiveFrom: string
  effectiveTo: string | null
  ageEvidenceRequired: AgeEvidenceRequired
  ageBands: JurisdictionAgeBandRow[]
  notes: string | null
}

export type ReadinessDimensionRow = GraphqlWireShape<ReadinessDimensionResult>

export type ReadinessItemRow = Omit<GraphqlWireShape<ReadinessItem>, 'timing'>

export type ClientCategoryReadiness = {
  equipmentCategory: PolicyEquipmentScope | null
  evaluatedSiteLocalDate: string
  age: ReadinessDimensionRow
  policy: ReadinessDimensionRow
  skinType: ReadinessDimensionRow
  blockingItems: ReadinessItemRow[]
  actionableItems: ReadinessItemRow[]
  upcomingItems: ReadinessItemRow[]
}

export type ClientPolicyComplianceData = {
  clientId: string
  siteId: string
  ageVerificationMethod: AgeVerificationMethod | null
  ageVerifiedAt: string | null
  dobVerified: boolean
  categories: ClientCategoryReadiness[]
}

export type PolicyComplianceSettingsData = Omit<
  GraphqlWireShape<PolicyComplianceSettingsView>,
  'pendingItemsLinkTtlHours'
>

export type PolicyComplianceSettingsInput = Partial<Omit<PolicyComplianceSettingsData, 'siteId'>>

export type ClientRelationRow = {
  counterpartyClientId: string
  name: string
  type: string
  relationshipSubtype: string | null
  status: string
}

export type ClientPolicyAgreementRow = {
  agreementId: string
  agreementName: string
  type: string
  status: string
  signedAt: string | null
  createdAt: string
}

export type ClientAgreementsPageRow = {
  agreementId: string
  agreementName: string
  status: string
  signedAt: string | null
  createdAt: string
  siteAgreementTemplateId: string | null
  sentAt: string | null
}

export type ClientAgreementsPage = {
  results: { row: ClientAgreementsPageRow }[]
  pageInfo: {
    hasNextPage: boolean
    startCursor: string | null
    endCursor: string | null
    totalRecords: number | null
  }
}

export type ClientAgreementsPageVariables = {
  clientId: string
  siteId: string | null
  filters?: Record<string, unknown>
  pageSize: number
  cursor?: string
  search?: string
}

export type PaperApprover = GraphqlWireShape<PaperFormApprover>

export type ApprovalPinStatusData = GraphqlWireShape<ApprovalPinStatus>

export type PaperScanUploadTicket = GraphqlWireShape<PaperScanUploadTicketShape>

export type PaperPolicyFormSlot = PaperPolicyFormSlotInput

export type RecordPaperPolicyFormVariables = {
  siteId: string
  clientId: string
  scanUploadId: string
  approverStaffUserId: string
  approverPin: string
  slots: PaperPolicyFormSlot[]
}

export type RecordPaperPolicyFormResult = {
  paperRecordId: string
  acceptances: { agreementId: string; acceptanceState: PolicyAcceptanceState; slotIndex: number }[]
}

export type ClientAgeVerificationResult = {
  clientId: string
  dateOfBirth: string | null
  ageVerificationMethod: AgeVerificationMethod | null
  ageVerifiedAt: string | null
  capacityMismatchAgreementIds: string[]
}

type CursorPage<TRow> = {
  results: { row: TRow }[]
  pageInfo: { hasNextPage: boolean; endCursor: string | null }
}

const MAX_PAGES = 20

const fetchAllPages = async <TRow>(
  fetchPage: (cursor: string | undefined) => Promise<CursorPage<TRow>>
): Promise<TRow[]> => {
  const rows: TRow[] = []
  let cursor: string | undefined
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const result: CursorPage<TRow> = await fetchPage(cursor)
    rows.push(...result.results.map((entry) => entry.row))
    if (!result.pageInfo.hasNextPage || !result.pageInfo.endCursor) {
      break
    }
    cursor = result.pageInfo.endCursor
  }
  return rows
}

const runQuery = async <TResponse>(
  query: string,
  variables: Record<string, unknown>
): Promise<TResponse> => (await gqlMutate({ mutation: query, variables })) as TResponse

export const getGatingSitePolicies = gqlQuery({
  key: POLICY_COMPLIANCE_QUERY_KEYS.sitePolicies,
  query: sitePoliciesForGatingQuery,
})

export const getSitePolicyRevisions = gqlQuery({
  key: POLICY_COMPLIANCE_QUERY_KEYS.sitePolicyRevisions,
  query: sitePolicyRevisionsQuery,
})

export const getJurisdictionRules = gqlQuery({
  key: POLICY_COMPLIANCE_QUERY_KEYS.jurisdictionRules,
  query: equipmentJurisdictionRulesQuery,
})

export const getClientPolicyCompliance = gqlQuery({
  key: POLICY_COMPLIANCE_QUERY_KEYS.clientPolicyCompliance,
  query: clientPolicyComplianceQuery,
})

export const getClientPolicyRelations = gqlQuery({
  key: POLICY_COMPLIANCE_QUERY_KEYS.clientPolicyRelations,
  query: clientPolicyRelationsQuery,
})

export const getPolicyComplianceSettings = gqlQuery({
  key: POLICY_COMPLIANCE_QUERY_KEYS.policyComplianceSettings,
  query: sitePolicyComplianceSettingsQuery,
})

export const updatePolicyComplianceSettings = async (variables: {
  siteId: string
  input: PolicyComplianceSettingsInput
}): Promise<PolicyComplianceSettingsData> =>
  (
    await runQuery<{ updateSitePolicyComplianceSettings: PolicyComplianceSettingsData }>(
      updateSitePolicyComplianceSettingsMutation,
      variables
    )
  ).updateSitePolicyComplianceSettings

export const getMyApprovalPinStatus = gqlQuery({
  key: POLICY_COMPLIANCE_QUERY_KEYS.myApprovalPinStatus,
  query: myApprovalPinStatusQuery,
})

export const fetchClientPolicyAgreements = (variables: {
  clientId: string
  siteId: string
}): Promise<ClientPolicyAgreementRow[]> =>
  fetchAllPages((cursor) =>
    runQuery<{ clientAgreementsWithPagination: CursorPage<ClientPolicyAgreementRow> }>(
      clientPolicyAgreementsQuery,
      cursor ? { ...variables, cursor } : variables
    ).then((response) => response.clientAgreementsWithPagination)
  )

export const fetchClientAgreementsPage = ({
  cursor,
  search,
  ...variables
}: ClientAgreementsPageVariables): Promise<ClientAgreementsPage> =>
  runQuery<{ clientAgreementsWithPagination: ClientAgreementsPage }>(clientAgreementsPageQuery, {
    ...variables,
    ...(cursor ? { cursor } : {}),
    ...(search ? { search } : {}),
  }).then((response) => response.clientAgreementsWithPagination)

export const fetchPaperApprovers = (siteId: string): Promise<PaperApprover[]> =>
  runQuery<{ paperFormApprovers: PaperApprover[] }>(paperApproversQuery, { siteId }).then(
    (response) => response.paperFormApprovers
  )

export const createGatingSitePolicy = async (variables: {
  siteId: string
  input: GatingSitePolicyInput
}): Promise<GatingSitePolicy> =>
  (
    await runQuery<{ createSitePolicy: GatingSitePolicy }>(
      createGatingSitePolicyMutation,
      variables
    )
  ).createSitePolicy

export const updateGatingSitePolicy = async (variables: {
  id: string
  siteId: string
  input: GatingSitePolicyUpdateInput
}): Promise<GatingSitePolicyUpdateResult> =>
  (
    await runQuery<{ updateSitePolicy: GatingSitePolicyUpdateResult }>(
      updateGatingSitePolicyMutation,
      variables
    )
  ).updateSitePolicy

export const deleteGatingSitePolicy = (variables: {
  id: string
  siteId: string
}): Promise<unknown> => runQuery(deleteGatingSitePolicyMutation, variables)

export const publishSitePolicyRevision = async (variables: {
  siteId: string
  sitePolicyId: string
  requireReacceptance: boolean
  effectiveFrom: string | null
}): Promise<SitePolicyPublishResult> =>
  (
    await runQuery<{ publishSitePolicyRevision: SitePolicyPublishResult }>(
      publishSitePolicyRevisionMutation,
      variables
    )
  ).publishSitePolicyRevision

export const cancelScheduledSitePolicyRevision = async (variables: {
  siteId: string
  sitePolicyId: string
}): Promise<SitePolicyRevisionRow> =>
  (
    await runQuery<{ cancelScheduledSitePolicyRevision: SitePolicyRevisionRow }>(
      cancelScheduledSitePolicyRevisionMutation,
      variables
    )
  ).cancelScheduledSitePolicyRevision

export const saveJurisdictionRule = async (variables: {
  siteId: string
  ruleId: string | null
  input: JurisdictionRuleInput
}): Promise<JurisdictionRuleRow> =>
  (
    await runQuery<{ saveEquipmentJurisdictionRule: JurisdictionRuleRow }>(
      saveEquipmentJurisdictionRuleMutation,
      variables
    )
  ).saveEquipmentJurisdictionRule

export const retireJurisdictionRule = async (variables: {
  siteId: string
  ruleId: string
  reason: string
}): Promise<JurisdictionRuleRow> =>
  (
    await runQuery<{ retireEquipmentJurisdictionRule: JurisdictionRuleRow }>(
      retireEquipmentJurisdictionRuleMutation,
      variables
    )
  ).retireEquipmentJurisdictionRule

export const setMyApprovalPin = async (variables: {
  pin: string
  currentPin: string | null
}): Promise<ApprovalPinStatusData> =>
  (await runQuery<{ setMyApprovalPin: ApprovalPinStatusData }>(setMyApprovalPinMutation, variables))
    .setMyApprovalPin

export const verifyMyApprovalPin = async (variables: {
  pin: string
  siteId: string | null
}): Promise<ApprovalPinStatusData> =>
  (
    await runQuery<{ verifyMyApprovalPin: ApprovalPinStatusData }>(
      verifyMyApprovalPinMutation,
      variables
    )
  ).verifyMyApprovalPin

export const verifyClientAge = async (variables: {
  siteId: string
  clientId: string
  dateOfBirth: string
  method: AgeVerificationMethod
}): Promise<ClientAgeVerificationResult> =>
  (
    await runQuery<{ verifyClientAge: ClientAgeVerificationResult }>(
      verifyClientAgeMutation,
      variables
    )
  ).verifyClientAge

export const createPaperScanUpload = async (variables: {
  siteId: string
  clientId: string
}): Promise<PaperScanUploadTicket> =>
  (
    await runQuery<{ createPaperScanUpload: PaperScanUploadTicket }>(
      createPaperScanUploadMutation,
      variables
    )
  ).createPaperScanUpload

export class PaperScanUploadError extends Error {}

export const uploadPaperScan = async (ticket: PaperScanUploadTicket, file: File): Promise<void> => {
  const headers = Object.fromEntries(ticket.headers.map((header) => [header.name, header.value]))
  const response = await fetch(ticket.uploadUrl, { method: 'PUT', body: file, headers })
  if (!response.ok) {
    throw new PaperScanUploadError(String(response.status))
  }
}

export const recordPaperPolicyForm = async (
  variables: RecordPaperPolicyFormVariables
): Promise<RecordPaperPolicyFormResult> =>
  (
    await runQuery<{ recordPaperPolicyForm: RecordPaperPolicyFormResult }>(
      recordPaperPolicyFormMutation,
      variables
    )
  ).recordPaperPolicyForm

export const withdrawElectronicConsent = (variables: {
  siteId: string
  clientId: string
  signerId: string
}): Promise<unknown> => runQuery(withdrawElectronicConsentMutation, variables)

export const withdrawGuardianPermission = (variables: {
  siteId: string
  clientId: string
  guardianSignerId: string
  reason: string | null
}): Promise<unknown> => runQuery(withdrawGuardianPermissionMutation, variables)

export const voidPolicyAcceptance = (variables: {
  siteId: string
  agreementId: string
  reason: string
}): Promise<unknown> => runQuery(voidPolicyAcceptanceMutation, variables)

export const revokeGuardianAuthority = (variables: {
  siteId: string
  clientId: string
  guardianClientId: string
  reason: string | null
}): Promise<unknown> => runQuery(revokeGuardianAuthorityMutation, variables)
